import { Link } from 'react-router';
import { MCP_TOOLS } from '@infras/shared-types';
import './McpConnectSteps.css';

/**
 * Live slot for docs/mcp.md (`<!-- docs:live component="McpConnectSteps" -->`), and the
 * body of step 2 on Get started.
 *
 * The three steps to connect an AI client, kept in ONE place so the two pages cannot
 * drift. It is a live slot because the health answer carries the live tool count.
 * Step 1 links to the Account page unless the token panel is already on screen next to
 * this component (`tokenPanelInline`, Get started only; a generated page passes no props).
 *
 * The command uses Claude Code's default LOCAL scope on purpose: the entry is stored for
 * the user alone, outside the repo. A project `.mcp.json` is meant to be committed, so a
 * token pasted there would leak (docs/mcp.md carries that warning).
 */
const ENTRYPOINT = 'https://infras-mcp.alphasphere.io/mcp';
const HEALTH_URL = 'https://infras-mcp.alphasphere.io/health';

// Three short lines: the block sits inside a list item and must not scroll sideways.
const CONNECT_CLI = `claude mcp add --transport http optisigns-infras \\
  ${ENTRYPOINT} \\
  --header "Authorization: Bearer <YOUR_TOKEN>"`;

const HEALTH_CHECK = `curl ${HEALTH_URL}
# → { "ok": true, "tools": ${MCP_TOOLS.length}, "authRequired": true }`;

export function McpConnectSteps({ tokenPanelInline = false }: { tokenPanelInline?: boolean }) {
  return (
    <ol className="doc-list doc-connect">
      <li>
        <strong>Create a personal access token.</strong>{' '}
        {tokenPanelInline ? (
          <>Use the token panel above.</>
        ) : (
          <>
            Open <Link to="/settings/profile">your Account page</Link> from the account menu, under Settings, and
            find <strong>Personal access tokens</strong>.
          </>
        )}{' '}
        Type a label such as <code>laptop</code>, click <strong>Create token</strong> and copy the{' '}
        <code>pat_…</code> value. It is shown once. If you lose it, revoke it and create a new one.
      </li>
      <li>
        <strong>Add the server to Claude Code.</strong> Run this in your project, with your token in place of{' '}
        <code>&lt;YOUR_TOKEN&gt;</code>. Claude Code stores the entry for you alone, outside the repo.
        <pre className="doc-code">
          <code>{CONNECT_CLI}</code>
        </pre>
      </li>
      <li>
        <strong>Check the connection.</strong> Start Claude Code in that project and run <code>/mcp</code>. It
        lists <code>optisigns-infras</code> as connected, with the tools your role allows. To check that the
        server is reachable at all:
        <pre className="doc-code">
          <code>{HEALTH_CHECK}</code>
        </pre>
      </li>
    </ol>
  );
}
